import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, TrendingUp, Target, Flame, BookOpen, Calendar, Sparkles, BarChart3 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export const Route = createFileRoute("/progress")({
  component: ProgressPage,
  head: () => ({
    meta: [
      { title: "Weekly Progress - Inquira" },
      { name: "description", content: "Track your weekly problem-solving progress and study streaks." },
      { property: "og:title", content: "Weekly Progress - Inquira" },
      { property: "og:description", content: "Track your weekly problem-solving progress and study streaks." },
      { property: "og:url", content: "https://www.inquira.online/progress" },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/progress" }],
  }),
});

interface DayData {
  date: string;
  count: number;
}

function ProgressPage() {
  const { user, isLoading: authLoading, streak } = useAuth();
  const [weekData, setWeekData] = useState<DayData[]>([]);
  const [totalSolved, setTotalSolved] = useState(0);
  const [conceptsCount, setConcepts] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchProgress = async () => {
      const now = new Date();
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 6);

      const { data: problems } = await supabase
        .from("solved_problems")
        .select("created_at, concept")
        .eq("user_id", user.id)
        .gte("created_at", weekAgo.toISOString())
        .order("created_at", { ascending: true });

      const { count } = await supabase
        .from("solved_problems")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      const { data: allConcepts } = await supabase
        .from("solved_problems")
        .select("concept")
        .eq("user_id", user.id);

      const uniqueConcepts = new Set(allConcepts?.map(p => p.concept).filter(Boolean));
      setConcepts(uniqueConcepts.size);
      setTotalSolved(count ?? 0);

      // Build 7-day data
      const days: DayData[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().split("T")[0];
        const dayCount = problems?.filter(p =>
          p.created_at.startsWith(dateStr)
        ).length ?? 0;
        days.push({ date: dateStr, count: dayCount });
      }
      setWeekData(days);
      setLoading(false);
    };

    fetchProgress();
  }, [user]);

  if (authLoading) return null;

  if (!user) {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <BarChart3 className="size-12 text-ink-muted mx-auto" />
          <p className="text-ink-muted">Sign in to view your progress</p>
          <Link to="/login" className="text-neon-pink font-semibold">Sign In</Link>
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...weekData.map(d => d.count), 1);
  const weekTotal = weekData.reduce((s, d) => s + d.count, 0);
  const daysActive = weekData.filter(d => d.count > 0).length;
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="min-h-screen notebook-lines">
      <div className="max-w-lg mx-auto px-4 pb-24">
        <header className="flex items-center gap-3 py-4">
          <Link to="/" aria-label="Back to home" title="Back to home" className="size-9 rounded-full bg-card border border-rule flex items-center justify-center hover:bg-rule/30 transition-colors">
            <ArrowLeft className="size-4 text-ink-muted" />
          </Link>
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-xl text-ink">
            Weekly Progress
          </h1>
        </header>

        {loading ? (
          <div className="flex flex-col items-center gap-4 py-12">
            <div className="size-12 rounded-full bg-neon-pink/10 flex items-center justify-center animate-pulse">
              <Sparkles className="size-6 text-neon-pink" />
            </div>
            <p className="text-sm text-ink-muted">Loading your stats...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3">
              <StatCard icon={<TrendingUp className="size-5 text-neon-pink" />} label="This Week" value={`${weekTotal}`} sub="problems solved" />
              <StatCard icon={<Flame className="size-5 text-neon-yellow" />} label="Streak" value={`${streak}`} sub="days" />
              <StatCard icon={<Target className="size-5 text-neon-blue" />} label="Concepts" value={`${conceptsCount}`} sub="mastered" />
              <StatCard icon={<BookOpen className="size-5 text-brand" />} label="All Time" value={`${totalSolved}`} sub="total solved" />
            </div>

            {/* Weekly chart */}
            <div className="bg-card rounded-2xl p-5 border border-rule shadow-sm">
              <h2 className="font-[family-name:var(--font-display)] font-bold text-sm text-ink mb-4 flex items-center gap-2">
                <Calendar className="size-4 text-ink-muted" /> Daily Activity
              </h2>
              <div className="flex items-end gap-2 h-32">
                {weekData.map((day) => {
                  const height = day.count > 0 ? Math.max(20, (day.count / maxCount) * 100) : 8;
                  const d = new Date(day.date + "T12:00:00");
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-ink">{day.count || ""}</span>
                      <div
                        className={`w-full rounded-t-lg transition-all ${day.count > 0 ? "bg-neon-pink" : "bg-rule/50"}`}
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-[10px] text-ink-muted">{dayNames[d.getDay()]}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Weekly goal */}
            <div className="bg-card rounded-2xl p-5 border border-rule shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-[family-name:var(--font-display)] font-bold text-sm text-ink">Weekly Goal</h2>
                <span className="text-xs font-bold text-neon-pink">{daysActive}/7 days active</span>
              </div>
              <Progress value={(daysActive / 7) * 100} className="h-3 bg-rule/30" />
              <p className="text-xs text-ink-muted">
                {daysActive >= 7
                  ? "🎉 Perfect week! You studied every day!"
                  : daysActive >= 5
                  ? "🔥 Great week! Almost perfect!"
                  : daysActive >= 3
                  ? "📚 Good effort! Keep pushing!"
                  : "💪 Try to study a little each day!"}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <div className="bg-card rounded-2xl p-4 border border-rule shadow-sm text-center space-y-1">
      <div className="flex items-center justify-center">{icon}</div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="text-2xl font-extrabold font-[family-name:var(--font-display)] text-ink">{value}</p>
      <p className="text-[11px] text-ink-muted">{sub}</p>
    </div>
  );
}
